import { useId } from "react"

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from "@coval/ui"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import { CustomSipHeadersField } from "@/components/shared/forms/CustomSipHeadersField"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"
import { PhoneNumberInput } from "@/components/shared/forms/PhoneNumberInput"
import { SipAddressInput } from "@/components/shared/forms/SipAddressInput"
import { useClearMediaEncryptionOutsideSipMode } from "@/hooks/useClearMediaEncryptionOutsideSipMode"
import { useConnectionMode } from "@/hooks/useConnectionMode"
import { useDispatchMode, type DispatchMode } from "@/hooks/useDispatchMode"
import { useLiveKitAdvancedConfig } from "@/hooks/useLiveKitAdvancedConfig"
import { useSipHeadersJson } from "@/hooks/useSipHeadersJson"
import {
  isMediaEncryptionValue,
  MediaEncryption,
} from "@/types/api/SimulatorType"
import {
  isValidPhoneNumber,
  isValidSipAddress,
} from "@/utils/persona/phoneNumberUtils"

interface BaseFormFieldProps {
  disabled?: boolean
  className?: string
}

/**
 * Info-tooltip copy for inbound voice agent arguments. Centralized so the
 * single- and multi-phone inbound field variants stay in sync.
 */
export const INBOUND_VOICE_TOOLTIPS = {
  connectionType:
    "How Coval connects to your inbound agent: a phone number (PSTN) or a SIP address for direct SIP trunking.",
  phoneNumber:
    "The phone number Coval dials to reach your inbound agent, in E.164 format (e.g. +12345678901).",
  sipAddress:
    "The SIP address Coval dials to reach your inbound agent (e.g. sip:agent@example.com).",
} as const

export const SMS_TOOLTIPS = {
  phoneNumber:
    "The phone number Coval texts to reach your SMS agent, in E.164 format (e.g. +14155551234).",
} as const

/**
 * Info-tooltip copy for LiveKit agent arguments. Mirrors
 * INBOUND_VOICE_TOOLTIPS so all agent forms read consistently.
 */
const LIVEKIT_TOOLTIPS = {
  tokenEndpoint:
    "Endpoint Coval sends a POST request to (with room_name and participant_name) to generate a LiveKit access token. Must be a valid HTTPS URL.",
  contentType:
    "The Content-Type header Coval sends on the token-generation request (e.g. application/json).",
  livekitUrl:
    "Your LiveKit server WebSocket URL (wss://…). If your token endpoint returns serverUrl, that value overrides this.",
  dispatchMode:
    "How your LiveKit agent joins the room. Automatic dispatch: your worker has no agent_name and auto-joins every new room — most setups use this by default. Explicit dispatch: your worker registers with an agent_name and only joins when the access token requests that same name.",
  sandboxId:
    "LiveKit's Sandbox and newer Development Token Server are the same underlying mechanism under two different names (LiveKit's own SDKs still call this parameter sandboxId internally). Useful for development/testing — e.g. quickly trying out a LiveKit agent without running your own token server. Not needed if you have a real production token endpoint. Leave empty if you're not using LiveKit's hosted sandbox/dev-token-server.",
  agentName:
    "Must match the agent_name your LiveKit agent worker registered under (via WorkerOptions in LiveKit's agents SDK) — not this Coval agent's own name above.",
  customHeaders:
    "Optional HTTP headers (JSON) sent with the token-generation request, e.g. an Authorization bearer token. Supports {{simulation_output_id}} (alias: {{simulation_id}}), {{test_case.*}}, {{agent.*}}, and {{run_metadata.*}}.",
  customPayload:
    "Optional JSON fields merged with room_name and participant_name when Coval calls your token endpoint. Supports {{simulation_output_id}} (alias: {{simulation_id}}), {{test_case.*}}, {{agent.*}}, and {{run_metadata.*}}.",
} as const

interface PhoneNumberFieldProps extends BaseFormFieldProps {
  value: string
  onChange: (value: string) => void
  labelTooltip?: React.ReactNode
}

export const PhoneNumberField: React.FC<PhoneNumberFieldProps> = ({
  value,
  onChange,
  disabled,
  labelTooltip,
}) => {
  const isInvalid = value && !isValidPhoneNumber(value)

  return (
    <div className="space-y-2">
      <FieldLabel required tooltip={labelTooltip}>
        Phone Number
      </FieldLabel>
      <PhoneNumberInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        error={isInvalid ? "invalid" : undefined}
      />
    </div>
  )
}

interface SipAddressFieldProps extends BaseFormFieldProps {
  value: string
  onChange: (value: string) => void
  widebandEnabled?: boolean
  onWidebandChange?: (enabled: boolean) => void
  labelTooltip?: React.ReactNode
}

export const SipAddressField: React.FC<SipAddressFieldProps> = ({
  value,
  onChange,
  widebandEnabled,
  onWidebandChange,
  disabled,
  className = "",
  labelTooltip,
}) => {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <FieldLabel required tooltip={labelTooltip}>
          SIP Address
        </FieldLabel>
        <SipAddressInput
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={className}
          error={value && !isValidSipAddress(value) ? "invalid" : undefined}
        />
      </div>
      {onWidebandChange && (
        <div className="flex items-center gap-2">
          <Switch
            checked={widebandEnabled ?? false}
            onCheckedChange={onWidebandChange}
            disabled={disabled}
            aria-label="Wideband Audio (16kHz)"
          />
          <span className="text-sm text-coval-text-muted">
            Wideband Audio (16kHz)
          </span>
        </div>
      )}
    </div>
  )
}

interface LiveKitFieldsProps extends BaseFormFieldProps {
  tokenEndpoint: string
  contentType: string
  livekitUrl: string
  agentName: string
  sandboxId: string
  customHeaders?: string
  customPayload?: string
  /**
   * The user's last dispatch-mode selection, persisted in
   * formData.metadata.livekit_dispatch_mode — a transient field stripped
   * before save (see getLiveKitMetadataForSave) so nothing new reaches the
   * backend. Passed through to useDispatchMode; see that hook for why this
   * exists instead of a useState local to this component.
   */
  dispatchModeSelection?: DispatchMode
  onTokenEndpointChange: (value: string) => void
  onContentTypeChange: (value: string) => void
  onLivekitUrlChange: (value: string) => void
  onAgentNameChange: (value: string) => void
  onSandboxIdChange: (value: string) => void
  onCustomHeadersChange?: (value: string) => void
  onCustomPayloadChange?: (value: string) => void
  onDispatchModeSelectionChange: (mode: DispatchMode) => void
}

export const LiveKitFields: React.FC<LiveKitFieldsProps> = ({
  tokenEndpoint,
  contentType,
  livekitUrl,
  agentName,
  sandboxId,
  customHeaders = "",
  customPayload = "",
  dispatchModeSelection,
  onTokenEndpointChange,
  onContentTypeChange,
  onLivekitUrlChange,
  onAgentNameChange,
  onSandboxIdChange,
  onCustomHeadersChange,
  onCustomPayloadChange,
  onDispatchModeSelectionChange,
  disabled,
  className = "",
}) => {
  const {
    showAdvanced,
    setShowAdvanced,
    localHeaders,
    localPayload,
    headersError,
    payloadError,
    handleHeadersChange,
    handlePayloadChange,
  } = useLiveKitAdvancedConfig({
    customHeaders,
    customPayload,
    onCustomHeadersChange,
    onCustomPayloadChange,
  })
  const {
    inputMode: dispatchMode,
    handleModeChange: handleDispatchModeChange,
  } = useDispatchMode({
    agentName,
    selectedMode: dispatchModeSelection,
    onAgentNameChange,
    onSelectedModeChange: onDispatchModeSelectionChange,
  })

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <FieldLabel required tooltip={LIVEKIT_TOOLTIPS.tokenEndpoint}>
          Token Generation Endpoint
        </FieldLabel>
        <Input
          value={tokenEndpoint}
          onChange={(e) => onTokenEndpointChange(e.target.value)}
          className={className}
          disabled={disabled}
          placeholder="https://cloud-api.livekit.io/api/sandbox/connection-details"
        />
      </div>
      <div className="space-y-2">
        <FieldLabel tooltip={LIVEKIT_TOOLTIPS.contentType}>
          Content Type
        </FieldLabel>
        <Input
          value={contentType}
          onChange={(e) => onContentTypeChange(e.target.value)}
          className={className}
          disabled={disabled}
          placeholder="application/json"
        />
      </div>
      <div className="space-y-2">
        <FieldLabel required tooltip={LIVEKIT_TOOLTIPS.livekitUrl}>
          LiveKit URL
        </FieldLabel>
        <Input
          value={livekitUrl}
          onChange={(e) => onLivekitUrlChange(e.target.value)}
          className={className}
          disabled={disabled}
          placeholder="wss://workshop-xyz.livekit.cloud"
        />
      </div>
      <div className="space-y-2">
        <FieldLabel tooltip={LIVEKIT_TOOLTIPS.dispatchMode}>
          Dispatch Mode
        </FieldLabel>
        <Select
          value={dispatchMode}
          onValueChange={handleDispatchModeChange}
          disabled={disabled}
        >
          <SelectTrigger aria-label="Dispatch Mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="automatic">Automatic dispatch</SelectItem>
            <SelectItem value="explicit">Explicit dispatch</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {dispatchMode === "explicit" ? (
        <div className="space-y-2">
          <FieldLabel required tooltip={LIVEKIT_TOOLTIPS.agentName}>
            Agent Name
          </FieldLabel>
          <Input
            value={agentName}
            onChange={(e) => onAgentNameChange(e.target.value)}
            className={className}
            disabled={disabled}
          />
          <p className="text-xs text-coval-text-muted">
            Required — your LiveKit agent must be registered under this name for
            explicit dispatch.
          </p>
        </div>
      ) : (
        <p className="text-xs text-coval-text-muted">
          Automatic dispatch — no agent name needed
        </p>
      )}
      {/* Advanced Configuration Section */}
      <div className="border-t border-coval-border pt-4">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-sm text-coval-text-muted hover:text-coval-text"
          disabled={disabled}
        >
          <span>{showAdvanced ? "▼" : "▶"}</span>
          <span>Advanced Configuration</span>
        </button>

        {showAdvanced && (
          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <FieldLabel tooltip={LIVEKIT_TOOLTIPS.sandboxId}>
                Sandbox / Dev Token Server ID (Testing Only)
              </FieldLabel>
              <Input
                value={sandboxId}
                onChange={(e) => onSandboxIdChange(e.target.value)}
                className={className}
                disabled={disabled}
                placeholder="workshop-xyz"
              />
              <p className="text-xs text-coval-text-muted">
                Optional, for development/testing only — sets the X-Sandbox-ID
                header LiveKit's hosted Sandbox / Development Token Server API
                uses to identify your project. Not a production setting; leave
                empty if you have your own token endpoint.
              </p>
            </div>
            <div className="space-y-2">
              <FieldLabel tooltip={LIVEKIT_TOOLTIPS.customHeaders}>
                Custom Headers (JSON)
              </FieldLabel>
              <JsonCodeEditor
                value={localHeaders}
                onChange={handleHeadersChange}
                className={className}
                placeholder={
                  '{\n  "X-API-Key": "your-api-key",\n  "X-Coval-Simulation-Id": "{{simulation_id}}"\n}'
                }
                rows={4}
                error={headersError}
                ariaLabel="Custom Headers JSON"
              />
            </div>

            <div className="space-y-2">
              <FieldLabel tooltip={LIVEKIT_TOOLTIPS.customPayload}>
                Custom Payload Fields (JSON)
              </FieldLabel>
              <JsonCodeEditor
                value={localPayload}
                onChange={handlePayloadChange}
                className={className}
                placeholder={
                  '{\n  "agent_variant": "maria_friendly",\n  "demo_type": "openai",\n  "room_config": {\n    "agents": [{\n      "agent_name": "my-agent"\n    }]\n  }\n}'
                }
                rows={6}
                error={payloadError}
                ariaLabel="Custom Payload Fields JSON"
              />
              <p className="text-xs text-coval-text-muted">
                These fields will be merged with room_name and participant_name
                in the token request
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

interface InboundVoiceFieldsProps extends BaseFormFieldProps {
  phoneNumber: string
  onPhoneNumberChange: (value: string) => void
  customSipHeaders?: string
  onCustomSipHeadersChange?: (value: string) => void
  mediaEncryption?: string
  onMediaEncryptionChange?: (value: string) => void
}

const MEDIA_ENCRYPTION_OPTIONS = [
  { label: "Disabled", value: MediaEncryption.DISABLED },
  { label: "SRTP", value: MediaEncryption.SRTP },
  { label: "DTLS", value: MediaEncryption.DTLS },
] as const

export const InboundVoiceFields: React.FC<InboundVoiceFieldsProps> = ({
  phoneNumber,
  onPhoneNumberChange,
  customSipHeaders,
  onCustomSipHeadersChange,
  mediaEncryption,
  onMediaEncryptionChange,
  disabled,
  className = "",
}) => {
  const connectionTypeId = useId()

  const parsedSipHeaders = useSipHeadersJson(customSipHeaders)

  const { inputMode, handleModeChange } = useConnectionMode({
    phoneNumber,
    onPhoneNumberChange,
  })

  const isSipMode = inputMode === "sip"
  useClearMediaEncryptionOutsideSipMode(
    isSipMode,
    phoneNumber,
    mediaEncryption,
    onMediaEncryptionChange,
  )
  const mediaEncryptionSelectValue =
    mediaEncryption == null || isMediaEncryptionValue(mediaEncryption)
      ? (mediaEncryption ?? MediaEncryption.DISABLED)
      : ""

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <FieldLabel
          htmlFor={connectionTypeId}
          tooltip={INBOUND_VOICE_TOOLTIPS.connectionType}
        >
          Connection Type
        </FieldLabel>
        <Select
          value={inputMode}
          onValueChange={(value) => handleModeChange(value)}
          disabled={disabled}
        >
          <SelectTrigger id={connectionTypeId}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="phone">Phone Number</SelectItem>
            <SelectItem value="sip">SIP Address</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {inputMode === "phone" ? (
        <PhoneNumberField
          value={phoneNumber}
          onChange={onPhoneNumberChange}
          disabled={disabled}
          className={className}
          labelTooltip={INBOUND_VOICE_TOOLTIPS.phoneNumber}
        />
      ) : (
        <>
          <div className="space-y-2">
            <FieldLabel required tooltip={INBOUND_VOICE_TOOLTIPS.sipAddress}>
              SIP Address
            </FieldLabel>
            <SipAddressInput
              value={phoneNumber}
              onChange={onPhoneNumberChange}
              disabled={disabled}
              className={className}
              error={
                phoneNumber && !isValidSipAddress(phoneNumber)
                  ? "invalid"
                  : undefined
              }
            />
          </div>
          {onCustomSipHeadersChange && (
            <CustomSipHeadersField
              value={parsedSipHeaders}
              onChange={onCustomSipHeadersChange}
            />
          )}
          {onMediaEncryptionChange && (
            <div className="space-y-2">
              <FieldLabel>Media Encryption</FieldLabel>
              <Select
                value={mediaEncryptionSelectValue}
                onValueChange={onMediaEncryptionChange}
                disabled={disabled}
              >
                <SelectTrigger aria-label="Media Encryption">
                  <SelectValue
                    placeholder={
                      mediaEncryptionSelectValue === ""
                        ? "Unsupported saved value; choose a replacement"
                        : undefined
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {MEDIA_ENCRYPTION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export {
  DeepgramRealtimeFields,
  GeminiRealtimeFields,
  GrokRealtimeFields,
  OpenAIRealtimeFields,
} from "./RealtimeSimulatorFormFields"
